import React, { useEffect, useRef, useState } from "react";

import { toast } from "react-toastify";

import { Edit3, Loader2, Plus, RefreshCw, Trash2, X } from "lucide-react";

import useBulkCatalogue from "../../services/Order/useBulkCatalogue";

import useBulkPackages from "../../services/Order/useBulkPackages";

const MENU_EMPTY = {
  id: "",

  menu_name: "",

  menu_icon: "",

  menu_sequence: "",

  is_active: "Y",

  catalog_id: "",
};

const MAP_EMPTY = {
  menu_id: "",

  package_id: "",

  display_order: "",

  is_primary: "0",
};

const field =
  "h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-emerald-400";

export function BulkMenus() {
  const {
    getBulkMenus,

    insertBulkMenu,

    updateBulkMenu,

    deleteBulkMenu,

    bulkMenuSaving,

    bulkMenusLoading,

    bulkMenuDeletingId,

    resolveBulkCatalogId,

    uploadBulkItemImage,

    itemImageUploading,
  } = useBulkCatalogue();

  const [rows, setRows] = useState([]),
    [form, setForm] = useState(MENU_EMPTY),
    [modal, setModal] = useState(false),
    [catalogIdLoading, setCatalogIdLoading] = useState(false);

  const [menuImageData, setMenuImageData] = useState("");

  const [menuImageReading, setMenuImageReading] = useState(false);

  const [menuSubmitBusy, setMenuSubmitBusy] = useState(false);

  const menuSubmitLockRef = useRef(false);

  const menuBusy =
    bulkMenuSaving || itemImageUploading || menuImageReading || menuSubmitBusy;

  const handleMenuImageChange = (e) => {
    const file = e.target.files?.[0];

    e.target.value = "";

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image");

      return;
    }

    setMenuImageReading(true);

    const reader = new FileReader();

    reader.onload = () => {
      const base64 = String(reader.result || "");

      if (!base64.startsWith("data:image/")) {
        toast.error("Invalid image");

        setMenuImageReading(false);

        return;
      }

      setMenuImageData(base64);

      setMenuImageReading(false);
    };

    reader.onerror = () => {
      toast.error("Unable to read selected image");

      setMenuImageReading(false);
    };

    reader.readAsDataURL(file);
  };

  const closeMenuModal = () => {
    if (menuSubmitLockRef.current || menuBusy) return;
    setModal(false);
    setMenuImageData("");
    setForm(MENU_EMPTY);
  };

  const load = async () => setRows(await getBulkMenus(""));

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!modal) return;

    let active = true;

    const loadCatalogId = async () => {
      setCatalogIdLoading(true);

      try {
        const catalogId = await resolveBulkCatalogId();

        if (active) {
          setForm((prev) => ({
            ...prev,

            catalog_id: String(catalogId ?? "").trim(),
          }));
        }
      } finally {
        if (active) setCatalogIdLoading(false);
      }
    };

    loadCatalogId();

    return () => {
      active = false;
    };
  }, [modal]);

  const edit = (r) => {
    setMenuImageData("");

    setForm({
      id: String(r?.id ?? ""),

      menu_name: String(r?.menu_name ?? ""),

      menu_icon: String(r?.menu_icon ?? ""),

      menu_sequence: String(r?.menu_sequence ?? ""),

      is_active: String(r?.is_active ?? "Y"),

      catalog_id: String(r?.catalog_id ?? ""),
    });

    setModal(true);
  };

  const submit = async (e) => {
    e.preventDefault();

    if (
      menuSubmitLockRef.current ||
      menuBusy ||
      catalogIdLoading ||
      !form.catalog_id
    ) {
      return;
    }

    menuSubmitLockRef.current = true;
    setMenuSubmitBusy(true);

    try {
      let finalMenuIcon = String(form.menu_icon || "").trim();

      // Upload the new image first. On failure, don't submit the menu API.
      if (menuImageData) {
        const uploadedUrl = await uploadBulkItemImage({
          item_name: form.menu_name.trim() || "bulk_menu",
          image_data: menuImageData,
        });
        if (!uploadedUrl) return;
        finalMenuIcon = uploadedUrl;
      }

      const payload = { ...form, menu_icon: finalMenuIcon };
      const ok = form.id
        ? await updateBulkMenu(payload)
        : await insertBulkMenu(payload);

      if (ok) {
        setModal(false);
        setForm(MENU_EMPTY);
        setMenuImageData("");
        await load();
      }
    } catch (error) {
      console.error("Bulk menu image/save error:", error);
      toast.error(error?.message || "Unable to save bulk menu");
    } finally {
      menuSubmitLockRef.current = false;
      setMenuSubmitBusy(false);
    }
  };

  return (
    <section className="space-y-3">
      <Header
        title="Bulk Menu"
        loading={bulkMenusLoading}
        onRefresh={load}
        onAdd={() => {
          setMenuImageData("");

          setForm(MENU_EMPTY);

          setModal(true);
        }}
        add="Add Menu"
      />

      <Rows
        loading={bulkMenusLoading}
        empty="No bulk menus found."
        rows={rows}
        render={(r, i) => (
          <div
            key={r?.id ?? i}
            className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0"
          >
            <div className="min-w-0 flex-1">
              <b className="text-sm text-slate-900">
                {r?.menu_name || "Unnamed Menu"}
              </b>

              <p className="text-[11px] text-slate-500">
                ID #{r?.id || "—"} · Catalog {r?.catalog_id || "—"} · Sequence{" "}
                {r?.menu_sequence || "—"} · {r?.is_active || "—"}
              </p>
            </div>

            <Icon onClick={() => edit(r)}>
              <Edit3 size={13} />
            </Icon>

            <Icon
              danger
              disabled={bulkMenuDeletingId === String(r?.id)}
              onClick={async () => {
                if (await deleteBulkMenu(r?.id)) load();
              }}
            >
              {bulkMenuDeletingId === String(r?.id) ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Trash2 size={13} />
              )}
            </Icon>
          </div>
        )}
      />

      {modal && (
        <Modal
          title={form.id ? "Edit Bulk Menu" : "Add Bulk Menu"}
          close={closeMenuModal}
        >
          <form onSubmit={submit}>
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
              <Input
                label="Menu Name"
                name="menu_name"
                value={form.menu_name}
                set={setForm}
              />

              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                  Menu Icon
                </label>

                <div className="flex items-center gap-4 rounded-xl border border-slate-200 p-3">
                  <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                    {menuImageData || form.menu_icon ? (
                      <img
                        src={menuImageData || form.menu_icon}
                        alt="Menu Icon Preview"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="text-xs text-slate-400">No Image</span>
                    )}
                  </div>

                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={handleMenuImageChange}
                      disabled={menuBusy}
                      className="block w-full text-xs text-slate-600

          file:mr-3 file:rounded-lg file:border-0

          file:bg-emerald-50 file:px-3 file:py-2

          file:font-semibold file:text-emerald-700

          hover:file:bg-emerald-100"
                    />

                    <p className="mt-2 text-xs text-slate-500">
                      {menuImageReading
                        ? "Reading image..."
                        : menuImageData
                          ? "New image selected"
                          : "Choose an image to upload"}
                    </p>
                  </div>
                </div>
              </div>

              <Input
                label="Menu Sequence"
                name="menu_sequence"
                value={form.menu_sequence}
                set={setForm}
              />

              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-700">
                  Catalog ID
                </label>

                <input
                  type="text"
                  value={
                    catalogIdLoading
                      ? "Fetching Catalog ID..."
                      : form.catalog_id || "Catalog ID unavailable"
                  }
                  readOnly
                  className="h-10 w-full rounded-xl border border-slate-200 bg-slate-100 px-3 text-sm text-slate-700 cursor-not-allowed"
                />
              </div>

              <Select
                label="Is Active"
                name="is_active"
                value={form.is_active}
                set={setForm}
                options={["Y", "N"]}
              />
            </div>

            <Footer
              saving={menuBusy}
              disabled={catalogIdLoading || !form.catalog_id}
              close={closeMenuModal}
              edit={!!form.id}
            />
          </form>
        </Modal>
      )}
    </section>
  );
}

export function BulkMenuPackageMappings() {
  const {
    getBulkMenus,

    getBulkMenuPackageMappings,

    insertBulkMenuPackageMapping,

    updateBulkMenuPackageMapping,

    deleteBulkMenuPackageMapping,

    bulkMenuPackageMappingSaving,

    bulkMenuPackageMappingsLoading,

    bulkMenuPackageMappingDeletingKey,
  } = useBulkCatalogue();

  const { getPackages, packagesLoading } = useBulkPackages();

  const [packages, setPackages] = useState([]);

  const [menus, setMenus] = useState([]),
    [rows, setRows] = useState([]),
    [form, setForm] = useState(MAP_EMPTY),
    [modal, setModal] = useState(false),
    [editing, setEditing] = useState(false);

  const load = async () => {
    const r = await getBulkMenuPackageMappings("", "");

    setRows(Array.isArray(r) ? r : []);
  };

  const ensureMenus = async () => {
    if (menus.length) return menus;

    const r = await getBulkMenus("");

    const next = Array.isArray(r) ? r : [];

    setMenus(next);

    return next;
  };

  const loadPackages = async () => {
    // getPackages defaults to the current catalogue mapping ID.

    const response = await getPackages("");

    const available = Array.isArray(response) ? response : [];

    setPackages(available);

    return available;
  };

  const normalizePrimary = (value) =>
    ["1", "Y", "TRUE"].includes(
      String(value ?? "0")
        .trim()

        .toUpperCase(),
    )
      ? "1"
      : "0";

  useEffect(() => {
    load();
  }, []);

  const edit = async (r) => {
    await Promise.all([ensureMenus(), loadPackages()]);

    setForm({
      menu_id: String(r?.menu_id ?? ""),

      package_id: String(r?.package_id ?? ""),

      display_order: String(r?.display_order ?? ""),

      is_primary: normalizePrimary(r?.is_primary),
    });

    setEditing(true);

    setModal(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const ok = editing
      ? await updateBulkMenuPackageMapping(form)
      : await insertBulkMenuPackageMapping(form);

    if (ok) {
      setModal(false);
      setEditing(false);
      setForm(MAP_EMPTY);
      await load();
    }
  };

  const menuName = (id) =>
    menus.find((x) => String(x?.id) === String(id))?.menu_name ||
    `Menu #${id || "—"}`;

  return (
    <section className="space-y-3">
      <Header
        title="Menu Package Mapping"
        loading={bulkMenuPackageMappingsLoading}
        onRefresh={load}
        onAdd={async () => {
          const [m] = await Promise.all([ensureMenus(), loadPackages()]);

          setForm({ ...MAP_EMPTY, menu_id: String(m?.[0]?.id ?? "") });

          setEditing(false);

          setModal(true);
        }}
        add="Add Mapping"
      />

      <Rows
        loading={bulkMenuPackageMappingsLoading}
        empty="No menu package mappings found."
        rows={rows}
        render={(r, i) => {
          const key = `${r?.menu_id}:${r?.package_id}`;

          return (
            <div
              key={key || i}
              className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0"
            >
              <div className="min-w-0 flex-1">
                <b className="text-sm text-slate-900">{menuName(r?.menu_id)}</b>

                <p className="text-[11px] text-slate-500">
                  Package #{r?.package_id || "—"} · Order{" "}
                  {r?.display_order || "—"} · Primary{" "}
                  {normalizePrimary(r?.is_primary)}
                </p>
              </div>

              <Icon onClick={() => edit(r)}>
                <Edit3 size={13} />
              </Icon>

              <Icon
                danger
                disabled={bulkMenuPackageMappingDeletingKey === key}
                onClick={async () => {
                  if (
                    await deleteBulkMenuPackageMapping(
                      r?.menu_id,

                      r?.package_id,
                    )
                  )
                    load();
                }}
              >
                {bulkMenuPackageMappingDeletingKey === key ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Trash2 size={13} />
                )}
              </Icon>
            </div>
          );
        }}
      />

      {modal && (
        <Modal
          title={
            editing ? "Edit Menu Package Mapping" : "Add Menu Package Mapping"
          }
          close={() => setModal(false)}
        >
          <form onSubmit={submit}>
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
              <label>
                <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                  Menu
                </span>

                <select
                  name="menu_id"
                  value={form.menu_id}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, menu_id: e.target.value }))
                  }
                  className={field}
                  disabled={editing}
                >
                  <option value="">Select Menu</option>

                  {menus.map((m) => (
                    <option key={m?.id} value={m?.id}>
                      {m?.menu_name || `Menu #${m?.id}`}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                  Package
                </span>

                <select
                  name="package_id"
                  value={form.package_id}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, package_id: e.target.value }))
                  }
                  className={field}
                  disabled={editing || packagesLoading}
                  required
                >
                  <option value="">
                    {packagesLoading
                      ? "Loading Packages..."
                      : packages.length
                        ? "Select Package"
                        : "No Packages Available"}
                  </option>

                  {editing &&
                    form.package_id &&
                    !packages.some(
                      (p) =>
                        String(p?.package_id ?? "") === String(form.package_id),
                    ) && (
                      <option value={form.package_id}>
                        Package #{form.package_id}
                      </option>
                    )}

                  {packages.map((pkg) => (
                    <option key={pkg.package_id} value={String(pkg.package_id)}>
                      {pkg.package_name || `Package #${pkg.package_id}`} (#
                      {pkg.package_id})
                    </option>
                  ))}
                </select>
              </label>

              <Input
                label="Display Order"
                name="display_order"
                value={form.display_order}
                set={setForm}
              />

              <Select
                label="Is Primary"
                name="is_primary"
                value={form.is_primary}
                set={setForm}
                options={["0", "1"]}
              />
            </div>

            <Footer
              saving={bulkMenuPackageMappingSaving}
              close={() => setModal(false)}
              edit={editing}
            />
          </form>
        </Modal>
      )}
    </section>
  );
}

function Header({ title, loading, onRefresh, onAdd, add }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div>
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>

        <p className="text-[11px] text-slate-500">
          Bulk catalogue package management
        </p>
      </div>

      <div className="flex gap-2">
        <button
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex h-8 items-center gap-1 rounded-lg border px-3 text-xs"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>

        <button
          onClick={onAdd}
          className="inline-flex h-8 items-center gap-1 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white"
        >
          <Plus size={13} />

          {add}
        </button>
      </div>
    </div>
  );
}

function Rows({ loading, empty, rows, render }) {
  if (loading)
    return <div className="h-24 animate-pulse rounded-xl border bg-white" />;

  if (!rows?.length)
    return (
      <div className="rounded-xl border border-dashed bg-white p-12 text-center text-sm text-slate-500">
        {empty}
      </div>
    );

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      {rows.map(render)}
    </div>
  );
}

function Modal({ title, close, children }) {
  return (
    <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/45 p-3">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="font-semibold">{title}</h3>

          <button onClick={close}>
            <X size={17} />
          </button>
        </div>

        {children}
      </div>
    </div>
  );
}

function Input({ label, name, value, set, disabled }) {
  return (
    <label>
      <span className="mb-1.5 block text-xs font-semibold text-slate-700">
        {label}
      </span>

      <input
        name={name}
        value={value}
        disabled={disabled}
        onChange={(e) => set((p) => ({ ...p, [name]: e.target.value }))}
        className={field}
      />
    </label>
  );
}

function Select({ label, name, value, set, options }) {
  return (
    <label>
      <span className="mb-1.5 block text-xs font-semibold text-slate-700">
        {label}
      </span>

      <select
        name={name}
        value={value}
        onChange={(e) => set((p) => ({ ...p, [name]: e.target.value }))}
        className={field}
      >
        {options.map((x) => (
          <option key={x} value={x}>
            {x}
          </option>
        ))}
      </select>
    </label>
  );
}

function Footer({ saving, disabled = false, close, edit }) {
  return (
    <div className="flex justify-end gap-2 border-t bg-slate-50 px-4 py-3">
      <button
        type="button"
        onClick={close}
        className="h-9 rounded-lg border bg-white px-4 text-xs font-semibold"
      >
        Cancel
      </button>

      <button
        disabled={saving || disabled}
        className="inline-flex h-9 items-center gap-2 rounded-lg bg-emerald-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
      >
        {saving ? (
          <Loader2 size={13} className="animate-spin" />
        ) : edit ? (
          <Edit3 size={13} />
        ) : (
          <Plus size={13} />
        )}{" "}
        {saving ? "Saving..." : edit ? "Update" : "Save"}
      </button>
    </div>
  );
}

function Icon({ children, onClick, danger, disabled }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex h-8 w-8 items-center justify-center rounded-lg border ${danger ? "border-rose-100 bg-rose-50 text-rose-600" : "border-slate-200 text-slate-500"}`}
    >
      {children}
    </button>
  );
}
